import { useExperimentsFromDualInstances } from '@linktr.ee/feature-flags-mfe/DualStatsigInstances';
import { StatsigClient } from '@statsig/js-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { recordPasskeyRegistrationRumVariant } from '@shared/utils/clientMonitoring';
import {
  PASSKEY_REGISTER_EXPERIMENT,
  PASSKEY_REGISTER_POST_LOGIN_EXPERIMENT,
} from '@shared/utils/passkeyRegistrationConstants';
import { reportClientError } from '@shared/utils/reportClientError';
import { isPasskeyRegistrationClientCapable } from './passkeyRegistrationCapability';
import { PASSKEY_INTERACTION_IDS, ScreenNames } from './screenMapping';
import type { DescopeScreenContext } from './types/descope';

const PASSKEY_REGISTER_VARIANT = 'offer_passkey';
const MIN_PLAUSIBLE_USER_CREATED_AT_MS = 1_000_000_000_000;
const USER_CREATED_AT_MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
// Source: @linktr.ee/feature-flags-mfe@5.7.3/dist/config.js documents this
// public Warehouse Native key as shared across environments. Its
// dist/providers/Statsig/StatsigDualInstanceFeatureFlagsProvider.js selects
// this same key for every stage and passes stage separately as environment.tier.
// The wrapper does not export the key or an awaitable authenticated-user
// transition, so this is used only to await that transition on the exact shared
// client; experiment reads/exposures still use the wrapper.
const WAREHOUSE_NATIVE_STATSIG_SDK_KEY =
  'client-T3D0OdrnxxbL1aZap8v3rz6qj6veTBw7W81aX8ULmjw';
const STATSIG_INITIALISATION_MAX_WAIT_MS = 1500;
const STATSIG_IDENTITY_UPDATE_TIMEOUT_MS = 3000;
const DECISION_INTERACTION_TIMEOUT_MS = 4000;
const DECISION_WATCHDOG_TIMEOUT_MS = 8000;

export const PASSKEY_REGISTRATION_CAPABILITY_FORM_KEY =
  'form.passkeysRegisterExperimentCapable';
export const USER_CREATED_AT_FORM_KEY = 'form.userCreatedAt';

type PasskeyRegistrationVariant = 'control' | 'offer_passkey';
type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;
type DecisionFailureFallback = () => boolean;

interface PasskeyRegistrationDecisionState {
  continueToControl: () => boolean;
  failed: boolean;
  retry: () => void;
}

interface PasskeyRegistrationExperimentApi {
  getExperimentFromWarehouseNative: (
    experiment: string
  ) => Record<string, unknown> | undefined;
  initialised: boolean;
  shouldEvaluate?: () => boolean;
}

interface PasskeyRegistrationExperimentConfig {
  errorReasonPrefix?: string;
  experiment: string;
  includeEmail?: boolean;
  reportMissingUserId?: boolean;
  screen?: string;
  userCreatedAt?: number;
}

export const isPasskeyRegistrationDecisionScreen = (
  screenName: string | undefined
): screenName is
  | ScreenNames.PASSKEY_REGISTRATION_DECISION
  | ScreenNames.PASSKEY_REGISTRATION_POST_LOGIN_DECISION =>
  screenName === ScreenNames.PASSKEY_REGISTRATION_DECISION ||
  screenName === ScreenNames.PASSKEY_REGISTRATION_POST_LOGIN_DECISION;

export const withPasskeyRegistrationCapability = <
  TPayload extends Record<string, unknown>,
>(
  payload: TPayload
): TPayload &
  Record<typeof PASSKEY_REGISTRATION_CAPABILITY_FORM_KEY, boolean> =>
  ({
    ...payload,
    [PASSKEY_REGISTRATION_CAPABILITY_FORM_KEY]:
      isPasskeyRegistrationClientCapable(),
  }) as TPayload &
    Record<typeof PASSKEY_REGISTRATION_CAPABILITY_FORM_KEY, boolean>;

export const preservePasskeyRegistrationCapability = <
  TPayload extends Record<string, unknown>,
>(
  payload: TPayload
): TPayload &
  Partial<Record<typeof PASSKEY_REGISTRATION_CAPABILITY_FORM_KEY, true>> =>
  (isPasskeyRegistrationClientCapable()
    ? {
        ...payload,
        [PASSKEY_REGISTRATION_CAPABILITY_FORM_KEY]: true,
      }
    : payload) as TPayload &
    Partial<Record<typeof PASSKEY_REGISTRATION_CAPABILITY_FORM_KEY, true>>;

export const getAuthenticatedLinktreeUserId = (
  context: DescopeScreenContext
): string | undefined => {
  const user = context.user;
  if (!user || typeof user !== 'object' || Array.isArray(user)) {
    return undefined;
  }

  const customAttributes = (user as Record<string, unknown>).customAttributes;
  if (
    !customAttributes ||
    typeof customAttributes !== 'object' ||
    Array.isArray(customAttributes)
  ) {
    return undefined;
  }

  const userId = (customAttributes as Record<string, unknown>).uuid;
  return typeof userId === 'string' && userId.length > 0 ? userId : undefined;
};

const getAuthenticatedEmail = (
  context: DescopeScreenContext
): string | undefined => {
  const email = context.user?.email;
  return typeof email === 'string' && email.length > 0 ? email : undefined;
};

const getUserCreatedAt = (
  context: DescopeScreenContext
): number | undefined => {
  const userCreatedAt = context.form?.[USER_CREATED_AT_FORM_KEY];

  return typeof userCreatedAt === 'number' &&
    Number.isSafeInteger(userCreatedAt) &&
    userCreatedAt >= MIN_PLAUSIBLE_USER_CREATED_AT_MS &&
    userCreatedAt <= Date.now() + USER_CREATED_AT_MAX_CLOCK_SKEW_MS
    ? userCreatedAt
    : undefined;
};

const getUserCreatedAtFailureReason = (value: unknown): string => {
  if (value === undefined || value === null) {
    return 'post-login-user-created-at-missing';
  }

  if (typeof value !== 'number') {
    return 'post-login-user-created-at-wrong-type';
  }

  if (!Number.isSafeInteger(value)) {
    return 'post-login-user-created-at-not-integer';
  }

  return 'post-login-user-created-at-out-of-range';
};

const getPasskeyRegistrationErrorContext = (
  config: PasskeyRegistrationExperimentConfig
): Record<string, string> => ({
  flow: 'universal-login',
  ...(config.screen === undefined ? {} : { screen: config.screen }),
});

const getPasskeyRegistrationErrorReason = (
  config: PasskeyRegistrationExperimentConfig,
  reason: string
): string =>
  config.errorReasonPrefix === undefined
    ? reason
    : `${config.errorReasonPrefix}-${reason}`;

const withTimeout = async <TValue>(
  promise: Promise<TValue>,
  timeoutMs: number,
  timeoutMessage: string
): Promise<TValue> =>
  await new Promise<TValue>((resolve, reject) => {
    const timeoutId = window.setTimeout(
      () => reject(new Error(timeoutMessage)),
      timeoutMs
    );

    void promise.then(
      value => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      error => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });

const resolveAuthenticatedPasskeyRegistrationVariant = async (
  context: DescopeScreenContext,
  experimentApi: PasskeyRegistrationExperimentApi | undefined,
  config: PasskeyRegistrationExperimentConfig
): Promise<PasskeyRegistrationVariant> => {
  if (!isPasskeyRegistrationClientCapable()) {
    return 'control';
  }

  const userID = getAuthenticatedLinktreeUserId(context);
  if (!userID) {
    if (config.reportMissingUserId) {
      reportClientError(
        'Post-login passkey registration user ID missing',
        new Error('Post-login passkey registration user ID missing'),
        {
          ...getPasskeyRegistrationErrorContext(config),
          reason: 'post-login-user-id-missing',
        }
      );
    }
    return 'control';
  }

  if (!experimentApi?.initialised) {
    return 'control';
  }

  try {
    // This is an intentional terminal identity transition: the flow now has an
    // authenticated Linktree UUID, so later evaluations should no longer use
    // the anonymous stableID. Select the exact Warehouse Native instance and
    // await the switch before the sanctioned wrapper records the exposure.
    const warehouseClient = StatsigClient.instance(
      WAREHOUSE_NATIVE_STATSIG_SDK_KEY
    );
    const currentContext = warehouseClient.getContext();
    if (currentContext.sdkKey !== WAREHOUSE_NATIVE_STATSIG_SDK_KEY) {
      reportClientError(
        'Passkey registration warehouse client mismatch',
        new Error('Passkey registration warehouse client mismatch'),
        {
          ...getPasskeyRegistrationErrorContext(config),
          reason: getPasskeyRegistrationErrorReason(
            config,
            'warehouse-client-mismatch'
          ),
        }
      );
      return 'control';
    }

    const currentUser = { ...currentContext.user };
    if (!config.includeEmail) {
      delete currentUser.email;
    }

    const authenticatedUser = {
      ...currentUser,
      userID,
      ...(config.includeEmail
        ? {
            // Signup keeps the established email-based dogfood behavior. The
            // higher-volume post-login population is allocated by UUID only.
            email: getAuthenticatedEmail(context),
          }
        : {}),
      ...(config.userCreatedAt === undefined
        ? {}
        : {
            custom: {
              ...currentContext.user.custom,
              userCreatedAt: config.userCreatedAt,
            },
          }),
    };

    await withTimeout(
      warehouseClient.updateUserAsync(authenticatedUser),
      STATSIG_IDENTITY_UPDATE_TIMEOUT_MS,
      'Statsig identity update timed out'
    );

    if (experimentApi.shouldEvaluate && !experimentApi.shouldEvaluate()) {
      return 'control';
    }

    const variant = experimentApi.getExperimentFromWarehouseNative(
      config.experiment
    )?.variant;

    return variant === PASSKEY_REGISTER_VARIANT
      ? PASSKEY_REGISTER_VARIANT
      : 'control';
  } catch {
    reportClientError(
      'Passkey registration experiment identity transition failed',
      new Error('Passkey registration experiment identity transition failed'),
      {
        ...getPasskeyRegistrationErrorContext(config),
        reason: getPasskeyRegistrationErrorReason(
          config,
          'authenticated-experiment-resolution-failed'
        ),
      }
    );
    return 'control';
  }
};

export const resolvePasskeyRegistrationVariant = async (
  context: DescopeScreenContext,
  experimentApi?: PasskeyRegistrationExperimentApi
): Promise<PasskeyRegistrationVariant> =>
  await resolveAuthenticatedPasskeyRegistrationVariant(context, experimentApi, {
    experiment: PASSKEY_REGISTER_EXPERIMENT,
    includeEmail: true,
    screen: ScreenNames.PASSKEY_REGISTRATION_DECISION,
  });

export const resolvePostLoginPasskeyRegistrationVariant = async (
  context: DescopeScreenContext,
  experimentApi?: PasskeyRegistrationExperimentApi
): Promise<PasskeyRegistrationVariant> => {
  if (!isPasskeyRegistrationClientCapable()) {
    return 'control';
  }

  const userCreatedAt = getUserCreatedAt(context);
  if (userCreatedAt === undefined) {
    const receivedValue = context.form?.[USER_CREATED_AT_FORM_KEY];
    reportClientError(
      'Post-login passkey registration creation time invalid',
      new Error('Post-login passkey registration creation time invalid'),
      {
        flow: 'universal-login',
        reason: getUserCreatedAtFailureReason(receivedValue),
        screen: ScreenNames.PASSKEY_REGISTRATION_POST_LOGIN_DECISION,
        type: receivedValue === null ? 'null' : typeof receivedValue,
      }
    );
    return 'control';
  }

  return await resolveAuthenticatedPasskeyRegistrationVariant(
    context,
    experimentApi,
    {
      errorReasonPrefix: 'post-login',
      experiment: PASSKEY_REGISTER_POST_LOGIN_EXPERIMENT,
      reportMissingUserId: true,
      screen: ScreenNames.PASSKEY_REGISTRATION_POST_LOGIN_DECISION,
      userCreatedAt,
    }
  );
};

export const usePasskeyRegistrationExperiment = (
  screenName: string | undefined,
  context: DescopeScreenContext,
  next: DescopeNext | undefined,
  onRepeatedFailure: DecisionFailureFallback
): PasskeyRegistrationDecisionState => {
  const { getExperimentFromWarehouseNative, initialised } =
    useExperimentsFromDualInstances();
  const [initialisationWaitExpired, setInitialisationWaitExpired] =
    useState(false);
  const experimentReady = initialised || initialisationWaitExpired;
  const handledRef = useRef(false);
  const manualControlInFlightRef = useRef(false);
  const contextRef = useRef(context);
  const nextRef = useRef(next);
  const experimentApiRef = useRef({
    getExperimentFromWarehouseNative,
    initialised,
  });
  const repeatedFailureFallbackRef = useRef(onRepeatedFailure);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  contextRef.current = context;
  nextRef.current = next;
  experimentApiRef.current = {
    getExperimentFromWarehouseNative,
    initialised,
  };
  repeatedFailureFallbackRef.current = onRepeatedFailure;

  const retry = useCallback(() => {
    handledRef.current = false;
    setFailed(false);
    setAttempt(value => value + 1);
  }, []);

  const continueToControl = useCallback((): boolean => {
    if (!isPasskeyRegistrationDecisionScreen(screenName)) {
      return false;
    }
    if (manualControlInFlightRef.current) {
      return true;
    }

    const currentNext = nextRef.current;
    if (!currentNext) {
      reportClientError(
        'Passkey registration control interaction unavailable',
        new Error('Passkey registration control interaction unavailable'),
        {
          flow: 'universal-login',
          reason: 'manual-control-missing-next',
          screen: screenName,
        }
      );
      return false;
    }

    const controlInteractionId =
      screenName === ScreenNames.PASSKEY_REGISTRATION_POST_LOGIN_DECISION
        ? PASSKEY_INTERACTION_IDS.REGISTER_POST_LOGIN_CONTROL
        : PASSKEY_INTERACTION_IDS.REGISTER_CONTROL;

    manualControlInFlightRef.current = true;
    setFailed(false);
    void withTimeout(
      currentNext(controlInteractionId, {}),
      DECISION_INTERACTION_TIMEOUT_MS,
      'Passkey registration control interaction timed out'
    )
      .catch(() => {
        reportClientError(
          'Passkey registration decision interaction failed',
          new Error('Passkey registration decision interaction failed'),
          {
            flow: 'universal-login',
            reason: 'manual-control-fallback',
            screen: screenName,
          }
        );
        setFailed(true);
      })
      .finally(() => {
        manualControlInFlightRef.current = false;
      });
    return true;
  }, [screenName]);

  useEffect(() => {
    if (!isPasskeyRegistrationDecisionScreen(screenName) || initialised) {
      setInitialisationWaitExpired(false);
      return;
    }

    const timeoutId = window.setTimeout(
      () => setInitialisationWaitExpired(true),
      STATSIG_INITIALISATION_MAX_WAIT_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [initialised, screenName]);

  useEffect(() => {
    if (!isPasskeyRegistrationDecisionScreen(screenName)) {
      handledRef.current = false;
      return;
    }

    if (!experimentReady || !nextRef.current || handledRef.current) {
      return;
    }

    handledRef.current = true;
    setFailed(false);
    let active = true;
    let fallbackStarted = false;
    let settled = false;
    let interactionInFlight = false;
    const decisionScreenName = screenName;
    const isPostLoginDecision =
      decisionScreenName ===
      ScreenNames.PASSKEY_REGISTRATION_POST_LOGIN_DECISION;
    const controlInteractionId = isPostLoginDecision
      ? PASSKEY_INTERACTION_IDS.REGISTER_POST_LOGIN_CONTROL
      : PASSKEY_INTERACTION_IDS.REGISTER_CONTROL;
    const offerInteractionId = isPostLoginDecision
      ? PASSKEY_INTERACTION_IDS.REGISTER_POST_LOGIN_OFFER
      : PASSKEY_INTERACTION_IDS.REGISTER_OFFER;

    const reportDecisionFailure = (reason: string): void => {
      reportClientError(
        'Passkey registration decision interaction failed',
        new Error('Passkey registration decision interaction failed'),
        {
          flow: 'universal-login',
          screen: decisionScreenName,
          reason,
        }
      );
    };

    const advance = async (interactionId: string): Promise<void> => {
      const currentNext = nextRef.current;
      if (!currentNext) {
        throw new Error('Passkey registration decision interaction missing');
      }

      interactionInFlight = true;
      try {
        await withTimeout(
          currentNext(interactionId, {}),
          DECISION_INTERACTION_TIMEOUT_MS,
          'Passkey registration decision interaction timed out'
        );
      } finally {
        interactionInFlight = false;
      }

      if (active) {
        settled = true;
        window.clearTimeout(watchdog);
      }
    };

    const advanceToControl = async (reason: string): Promise<void> => {
      if (!active || fallbackStarted) {
        return;
      }

      fallbackStarted = true;
      try {
        await advance(controlInteractionId);
      } catch {
        reportDecisionFailure(reason);
        if (active) {
          if (attempt > 0) {
            if (!repeatedFailureFallbackRef.current()) {
              setFailed(true);
            }
          } else {
            setFailed(true);
          }
        }
      }
    };

    const watchdog = window.setTimeout(() => {
      if (!active || settled || fallbackStarted || interactionInFlight) {
        return;
      }

      reportDecisionFailure('decision-watchdog');
      void advanceToControl('watchdog-control-fallback');
    }, DECISION_WATCHDOG_TIMEOUT_MS);

    const resolveVariant = isPostLoginDecision
      ? resolvePostLoginPasskeyRegistrationVariant
      : resolvePasskeyRegistrationVariant;

    void resolveVariant(contextRef.current, {
      ...experimentApiRef.current,
      shouldEvaluate: () => active && !settled && !fallbackStarted,
    }).then(async variant => {
      if (!active || settled || fallbackStarted) {
        return;
      }

      recordPasskeyRegistrationRumVariant(
        variant,
        isPostLoginDecision
          ? PASSKEY_REGISTER_POST_LOGIN_EXPERIMENT
          : PASSKEY_REGISTER_EXPERIMENT
      );

      const interactionId =
        variant === PASSKEY_REGISTER_VARIANT
          ? offerInteractionId
          : controlInteractionId;

      try {
        await advance(interactionId);
      } catch {
        reportDecisionFailure('initial-decision');
        await advanceToControl('control-fallback');
      }
    });

    return () => {
      active = false;
      window.clearTimeout(watchdog);
    };
  }, [attempt, experimentReady, screenName]);

  return { continueToControl, failed, retry };
};
